import { useLocalSearchParams } from 'expo-router';
import { useMemo } from 'react';
import { Platform, StyleSheet, View } from 'react-native';

import {
  Button,
  IconChevronLeft,
  IconClose,
  ItemStepper,
  Skeleton,
  StepProps,
  Text,
  tokens,
} from '@fhs/ui';
import { isWeb } from '@fhs/utils';

import { useOfferDetailUi } from '../../queries/loyalty.queries';
import {
  useIsAvailableCartItemsInCart,
  useIsItemInCart,
  useIsLoadingTierSelector,
  useOfferCart,
  useSelectedStep,
  useShouldRenderTierSelector,
} from '../../state/offer-guide-cart-state';

interface NavigationProps {
  onPressPrev: () => void;
  onPressStep: (index: number) => void;
  onClose: () => void;
}

const CenterContent = ({
  onPressStep,
  isItemInCart,
  shouldRenderTierSelector,
}: {
  onPressStep: (index: number) => void;
  isItemInCart: boolean;
  shouldRenderTierSelector: boolean;
}) => {
  const { offerId } = useLocalSearchParams<{ offerId: string }>();
  const { isLoading } = useOfferDetailUi(offerId);
  const { steps: offerSteps, currentStepIndex, offerInfo } = useOfferCart();
  const selectedStep = useSelectedStep();

  const steps = useMemo(
    () =>
      offerSteps.map<StepProps>((offerStep, index) => ({
        image: offerStep?.selectedEntryImage,
        isActive: currentStepIndex === index,
        disabled: currentStepIndex === index,
      })),
    [offerSteps, currentStepIndex]
  );
  const hasOneStep = steps.length === 1;

  if (isLoading) {
    return <Skeleton style={styles.itemStepperSkeleton} />;
  }
  if (isItemInCart || !offerInfo?.isAvailable) {
    return null;
  }

  if (shouldRenderTierSelector) {
    const tierLabel = 'Select your discount tier';
    return (
      <View
        accessibilityLabel={tierLabel}
        accessibilityRole="header"
        accessible
        focusable
        style={{ alignSelf: 'center' }}
        {...(Platform.OS === 'web'
          ? ({
              tabIndex: 0,
            } as Record<string, unknown>)
          : {})}
      >
        <Text.Ui accessible={false} size="md" weight="semibold">
          Select Your Discount Tier
        </Text.Ui>
      </View>
    );
  }

  if (hasOneStep) {
    const oneStepLabel = selectedStep?.navTitle ?? 'Item selection';
    return (
      <View
        accessibilityLabel={oneStepLabel}
        accessibilityRole="header"
        accessible
        focusable
        style={{ alignSelf: 'center' }}
        {...(Platform.OS === 'web'
          ? ({
              tabIndex: 0,
            } as Record<string, unknown>)
          : {})}
      >
        <Text.Ui accessible={false} size="md" weight="semibold">
          {oneStepLabel}
        </Text.Ui>
      </View>
    );
  }

  return <ItemStepper steps={steps} onStepClick={onPressStep} />;
};

export function Navigation({ onPressPrev, onPressStep, onClose }: NavigationProps) {
  const { currentStepIndex, selectedEligibleItem, offerInfo } = useOfferCart();
  const isItemInCart = useIsItemInCart();
  const isAvailableCartItemsInCart = useIsAvailableCartItemsInCart();
  const shouldRenderTierSelector = useShouldRenderTierSelector();
  const isLoadingTierSelector = useIsLoadingTierSelector();
  const hideBackButton = isLoadingTierSelector || shouldRenderTierSelector;

  const isBackButtonDisabled =
    (currentStepIndex === 0 && !selectedEligibleItem && offerInfo?.tiers == null) ||
    shouldRenderTierSelector;

  if ((isItemInCart || isAvailableCartItemsInCart) && !isWeb) {
    return <View style={{ paddingTop: 20 }} />;
  }

  return (
    <View style={styles.navigation}>
      <Button
        key={`${shouldRenderTierSelector}-${isBackButtonDisabled}`}
        accessibilityElementsHidden={hideBackButton}
        accessibilityLabel="Go back one step in offer selection"
        accessibilityRole="button"
        disabled={isBackButtonDisabled}
        importantForAccessibility={hideBackButton ? 'no-hide-descendants' : 'auto'}
        type="unstyled"
        size="xl"
        onPress={onPressPrev}
        style={{ alignSelf: 'center', opacity: hideBackButton ? 0 : 1 }}
        {...(Platform.OS === 'web' && hideBackButton
          ? ({
              tabIndex: -1,
            } as Record<string, unknown>)
          : {})}
      >
        <Button.Icon>
          <IconChevronLeft
            accessibilityElementsHidden
            aria-hidden
            importantForAccessibility="no-hide-descendants"
            size={24}
          />
        </Button.Icon>
      </Button>
      <View style={styles.itemStepperContainer}>
        <CenterContent
          onPressStep={onPressStep}
          isItemInCart={isItemInCart || isAvailableCartItemsInCart}
          shouldRenderTierSelector={shouldRenderTierSelector}
        />
      </View>
      <Button
        accessibilityHint="May prompt to confirm if you have unsaved selections"
        accessibilityLabel="Close offer selection"
        accessibilityRole="button"
        type="ghost"
        size="xl"
        onPress={onClose}
        style={{ alignSelf: 'center' }}
        {...(Platform.OS === 'web'
          ? ({
              'aria-label': 'Close offer selection',
            } as Record<string, unknown>)
          : {})}
      >
        <Button.Icon>
          <IconClose size={24} />
        </Button.Icon>
      </Button>
    </View>
  );
}

const styles = StyleSheet.create({
  itemStepperContainer: {
    justifyContent: 'center',
    alignSelf: 'center',
    flexGrow: 1,
    flex: 1,
    overflow: 'hidden',
  },
  itemStepperSkeleton: {
    width: 75,
    height: 14,
    backgroundColor: tokens.colors.$houseLight,
    alignSelf: 'center',
  },
  navigation: {
    flexDirection: 'row',
    paddingHorizontal: 4,
    height: 66,
  },
});
